import { IconButton } from "@mui/material";
import * as React from "react";

import { RobotoTooltip } from "@/shared/components";
import { AttachFileIcon } from "@/shared/foundation/icons/mui";

import styles from "./AgentThreadInputBox.module.css";

interface Props {
  /** Disables the button, and is shown as its tooltip and accessible description. */
  disabledReason?: string;
  onFiles: (files: File[]) => void;
}

/** Paperclip button that opens the file picker for the thread's attachments. */
export function AttachFilesButton({ disabledReason, onFiles }: Props) {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const reasonId = React.useId();
  const disabled = disabledReason !== undefined;

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        aria-label="Files to attach"
        onChange={(event) => {
          if (event.target.files !== null) {
            // Copied out because clearing the value below empties the input's own list.
            onFiles(Array.from(event.target.files));
          }
          // Clearing the value lets the same file be chosen again.
          event.target.value = "";
        }}
      />
      {/* The tooltip wraps a span, not the button: a disabled button fires no
          pointer events, so a tooltip listening on it would never open while
          the reason it shows is most needed (and MUI logs an error for it). */}
      <RobotoTooltip title={disabledReason ?? "Attach files"}>
        <span>
          <IconButton
            size="small"
            aria-label="Attach files"
            aria-describedby={disabled ? reasonId : undefined}
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          >
            <AttachFileIcon className={styles.smallIcon} />
          </IconButton>
        </span>
      </RobotoTooltip>
      {disabled && (
        <span id={reasonId} className="sr-only">
          {disabledReason}
        </span>
      )}
    </>
  );
}
